/** One crew definition, reduced to what deciding an overlap needs. */
export interface StaffingCandidate {
	id: string;
	displayName: string | null;
	startDate: string;
	endDate: string | null;
	_members: Array<{ userId: string }>;
}

/** One person standing in two crews at once, and the days both crews claim them. */
export interface StaffingConflict {
	userId: string;
	crewDefinitionId: string;
	crewName: string | null;
	overlapStart: string;
	overlapEnd: string | null;
}

/** What the caller is checking: the crew being edited, its span, and the people on it. */
export interface StaffingConflictQuery {
	crewDefinitionId: string | null;
	startDate: string;
	endDate: string | null;
	userIds: string[];
}

/**
 * The crews that already claim one of these people over a day this span also claims.
 *
 * Two crews overlap when neither ends before the other begins, and a null end date means the crew
 * runs on — so it is treated as later than every date rather than as missing. The crew being
 * edited is excluded by id, or the form would report every saved member as a conflict with itself.
 *
 * This is a warning and not a rule: a person genuinely can be moved between crews mid-span, and
 * the database refuses none of it. What it prevents is the silent version, where the second crew
 * is planned without anyone noticing the first still has them.
 *
 * @param candidates - Every crew definition the organisation holds.
 * @param query - The crew being checked, its span and its people.
 * @returns One conflict per person and clashing crew, ascending by person then crew.
 */
export function findStaffingConflicts(
	candidates: StaffingCandidate[],
	query: StaffingConflictQuery,
): StaffingConflict[] {
	if (!query.userIds.length) return [];
	const wanted = new Set(query.userIds);
	const conflicts: StaffingConflict[] = [];

	for (const candidate of candidates) {
		if (candidate.id === query.crewDefinitionId) continue;
		if (!overlaps(query.startDate, query.endDate, candidate.startDate, candidate.endDate)) continue;

		for (const member of candidate._members) {
			if (!wanted.has(member.userId)) continue;
			conflicts.push({
				userId: member.userId,
				crewDefinitionId: candidate.id,
				crewName: candidate.displayName,
				overlapStart: laterOf(query.startDate, candidate.startDate),
				overlapEnd: earlierOf(query.endDate, candidate.endDate),
			});
		}
	}

	return conflicts;
}

/** Whether two closed-or-open-ended spans share a day. */
function overlaps(
	startA: string,
	endA: string | null,
	startB: string,
	endB: string | null,
): boolean {
	if (endA && endA < startB) return false;
	if (endB && endB < startA) return false;
	return true;
}

function laterOf(a: string, b: string): string {
	return a > b ? a : b;
}

/** Null is the open end, so it loses to any date: the shared span stops where the earlier one does. */
function earlierOf(a: string | null, b: string | null): string | null {
	if (!a) return b;
	if (!b) return a;
	return a < b ? a : b;
}
